/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Kurban ve Hisse Servisi
 * ───────────────────────────────────────────────────────────────
 * Firestore veritabanı işlemleri, Transaction yönetimi
 * ═══════════════════════════════════════════════════════════════
 */

import { collection, doc, runTransaction, serverTimestamp, setDoc, getDoc, updateDoc, writeBatch, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { formatPhone } from '../context/AuthContext';

/**
 * Kurban Fotoğrafı Yükleme (Hata durumunda null döner)
 */
export const uploadAnimalPhoto = async (file, tagNo) => {
  if (!file) return null;
  try {
    const fileExtension = file.name.split('.').pop();
    const fileName = `animals/${tagNo}_${Date.now()}.${fileExtension}`;
    const storageRef = ref(storage, fileName);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.error('Fotoğraf yükleme hatası:', error);
    throw new Error('Fotoğraf yüklenemedi: ' + error.message);
  }
};

/**
 * Makbuz (Dekont) Fotoğrafı/Dosyası Yükleme
 */
export const uploadReceiptPhoto = async (file) => {
  if (!file) return null;
  try {
    const fileExtension = file.name.split('.').pop();
    const fileName = `receipts/${Date.now()}_${Math.floor(Math.random()*1000)}.${fileExtension}`;
    const storageRef = ref(storage, fileName);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.error('Dekont yükleme hatası:', error);
    throw new Error('Dosya yüklenirken hata oluştu. İnternetinizi veya dosya formatını kontrol edin.');
  }
};

/**
 * Yeni Kurban Ekleme
 * @param {Object} animalData - Kurban bilgileri (küpe no, tür, fiyat vb.)
 * @param {string} createdBy - Ekleyen kullanıcının UID'si
 */
export const addAnimal = async (animalData, createdBy) => {
  const animalRef = doc(collection(db, 'animals'));
  
  const newAnimal = {
    ...animalData,
    totalShares: Number(animalData.totalShares) || (animalData.type === 'buyukbas' ? 7 : 1),
    shareCount: 0,
    status: 'active',
    createdAt: serverTimestamp(),
    createdBy,
  };

  await setDoc(animalRef, newAnimal);
  return { id: animalRef.id, ...newAnimal };
};

/**
 * Kurban Bilgilerini Güncelleme
 * @param {string} animalId - Kurban ID'si
 * @param {Object} updateData - Güncellenecek alanlar
 */
export const updateAnimal = async (animalId, updateData) => {
  const animalRef = doc(db, 'animals', animalId);
  const dataToUpdate = { ...updateData };
  // dataToUpdate.weight string kalabilir (600-650 gibi)
  if (dataToUpdate.price) dataToUpdate.price = Number(dataToUpdate.price);
  
  await updateDoc(animalRef, dataToUpdate);
};

/**
 * Hisse Ekleme (Transaction ile 7/7 kontrolü)
 * @param {string} animalId - Hisse eklenecek kurbanın ID'si
 * @param {Object} shareData - Hisse kayıt bilgileri
 * @param {string} createdBy - Ekleyen admin/superAdmin UID'si
 */
export const addShare = async (animalId, shareData, createdBy) => {
  // Vekalet kontrolü - (Kullanıcı dilerse sonra alabileceğinden bu engel kaldırıldı)  // Telefon standardizasyonu
  if (!shareData.customerPhone || shareData.customerPhone.trim() === '') {
    throw new Error('Müşteri telefon numarası zorunludur.');
  }
  const standardizedPhone = formatPhone(shareData.customerPhone);
  if (standardizedPhone.length < 10) {
    throw new Error('Geçerli bir telefon numarası giriniz.');
  }

  try {
    const newShareData = await runTransaction(db, async (transaction) => {
      const animalRef = doc(db, 'animals', animalId);
      const animalDoc = await transaction.get(animalRef);
      
      if (!animalDoc.exists()) {
        throw new Error('Kurban bulunamadı.');
      }

      const animal = animalDoc.data();
      
      const shareWeight = Number(shareData.shareWeight) || 1;

      // Stok/Hisse Limit Kontrolü
      if (animal.shareCount + shareWeight > animal.totalShares) {
        throw new Error(`Bu kurbanın hisseleri dolmuştur veya kalan boşluk (${animal.totalShares - animal.shareCount}) talep edileni (${shareWeight}) karşılamıyor.`);
      }

      // Kurbanın hisse sayısını artır
      const newShareCount = animal.shareCount + shareWeight;
      const newStatus = newShareCount >= animal.totalShares ? 'completed' : 'active';
      
      transaction.update(animalRef, {
        shareCount: newShareCount,
        status: newStatus
      });

      // Hisse dokümanını oluştur
      const shareRef = doc(collection(db, 'shares'));
      const sharePayload = {
        animalId,
        branch: animal.branch || null,
        customerFirstName: shareData.customerFirstName.trim(),
        customerLastName: shareData.customerLastName.trim(),
        customerPhone: standardizedPhone,
        isProxyGiven: shareData.isProxyGiven || false,
        shareWeight: shareWeight,
        price: shareData.price || 0,
        originalPrice: shareData.originalPrice || shareData.price || 0,
        discountAmount: shareData.discountAmount || 0,
        paymentStatus: shareData.paymentStatus || 'pending', // pending, partial, paid
        paidAmount: shareData.paidAmount || 0,
        createdAt: serverTimestamp(),
        createdBy,
      };

      transaction.set(shareRef, sharePayload);

      return { id: shareRef.id, ...sharePayload, animalShareCount: newShareCount };
    });

    return newShareData;
  } catch (error) {
    console.error('Add Share Transaction Error:', error);
    throw error;
  }
};

export const updateShare = async (shareId, shareData) => {
  try {
    const shareRef = doc(db, 'shares', shareId);
    
    // Temel güncellenebilecek alanlar
    const updatePayload = {
      customerFirstName: shareData.customerFirstName.trim(),
      customerLastName: shareData.customerLastName.trim(),
      customerPhone: formatPhone(shareData.customerPhone),
      isProxyGiven: !!shareData.isProxyGiven,
      updatedAt: serverTimestamp()
    };
    
    // Eğer düzenleme sırasında fiyat veya indirim güncellenmişse bunları da ekle
    if (shareData.price !== undefined) updatePayload.price = shareData.price;
    if (shareData.originalPrice !== undefined) updatePayload.originalPrice = shareData.originalPrice;
    if (shareData.discountAmount !== undefined) updatePayload.discountAmount = shareData.discountAmount;

    await updateDoc(shareRef, updatePayload);
  } catch (error) {
    console.error('Update Share Error:', error);
    throw error;
  }
};

/**
 * Kurban Detayı Getir
 
 */
export const getAnimalDetails = async (animalId) => {
  const docRef = doc(db, 'animals', animalId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

/**
 * Tahsilat/Ödeme Ekleme (Transaction ile bakiye kontrolü)
 */
export const addPayment = async (shareId, paymentData, createdBy) => {
  if (!shareId || !paymentData.amount || paymentData.amount <= 0) {
    throw new Error('Geçersiz tahsilat tutarı.');
  }

  try {
    const newPayment = await runTransaction(db, async (transaction) => {
      const shareRef = doc(db, 'shares', shareId);
      const shareDoc = await transaction.get(shareRef);
      
      if (!shareDoc.exists()) {
        throw new Error('Hissedar bulunamadı.');
      }

      // Numaratör Sayacı Ataması (Makbuz No)
      const counterRef = doc(db, 'system', 'counters');
      const counterDoc = await transaction.get(counterRef);
      let nextReceiptNumber = 10001; // Başlangıç değeri
      
      if (counterDoc.exists() && counterDoc.data().receiptCounter) {
         nextReceiptNumber = counterDoc.data().receiptCounter + 1;
         transaction.update(counterRef, { receiptCounter: nextReceiptNumber });
      } else {
         transaction.set(counterRef, { receiptCounter: nextReceiptNumber }, { merge: true });
      }
      
      const currentYear = new Date().getFullYear();
      const receiptNoStr = `MKBZ-${currentYear}-${nextReceiptNumber}`;

      const share = shareDoc.data();
      const currentPaid = share.paidAmount || 0;
      const amountToPay = Number(paymentData.amount);
      const totalPrice = share.price || 0;
      
      const newPaidAmount = currentPaid + amountToPay;
      
      if (totalPrice > 0 && newPaidAmount > totalPrice) {
        throw new Error(`Tahsilat tutarı kalan borçtan büyük olamaz. Max eklenebilecek: ${totalPrice - currentPaid} ₺`);
      }

      let newStatus = 'partial';
      if (totalPrice > 0 && newPaidAmount >= totalPrice) {
        newStatus = 'paid';
      } else if (newPaidAmount === 0) {
        newStatus = 'pending';
      }

      // Hissedar dokümanını güncelle
      transaction.update(shareRef, {
        paidAmount: newPaidAmount,
        paymentStatus: newStatus
      });

      // Ödeme dokümanı oluştur
      const paymentRef = doc(collection(db, 'payments'));
      const paymentPayload = {
        receiptNo: receiptNoStr, // Sistem tarafından üretilen eşsiz seri no
        shareId: shareId,
        amount: amountToPay,
        type: paymentData.type || 'Nakit', // Nakit, Kredi Kartı, Havale/EFT
        description: paymentData.description?.trim() || '', // Açıklama/Not eklendi
        receiptUrl: paymentData.receiptUrl || null, // Varsa dekont url'si
        createdAt: serverTimestamp(),
        createdBy: createdBy,
      };

      transaction.set(paymentRef, paymentPayload);

      return { id: paymentRef.id, ...paymentPayload };
    });

    return newPayment;
  } catch (error) {
    console.error('Add Payment Error:', error);
    throw error;
  }
};

/**
 * Tahsilat Silme (Transaction ile bakiye iadesi + Silinenler Arşivine Kopyalama)
 */
export const deletePayment = async (paymentId, shareId, amountToDeduct, deletedByEmail) => {
  try {
    await runTransaction(db, async (transaction) => {
      // 1. DOKÜMAN REFERANSLARI
      const paymentRef = doc(db, 'payments', paymentId);
      const shareRef = doc(db, 'shares', shareId);
      const deletedPaymentRef = doc(db, 'deleted_payments', paymentId);

      // 2. TÜM OKUMA (GET) İŞLEMLERİ - FİREBASE KURALI GEREĞİ ÖNCEKİ OLMALI!
      const paymentDoc = await transaction.get(paymentRef);
      const shareDoc = await transaction.get(shareRef);
      
      if (!paymentDoc.exists()) {
        throw new Error('Silinecek tahsilat bulunamadı.');
      }
      
      const paymentData = paymentDoc.data();

      // 3. TÜM YAZMA (SET/UPDATE/DELETE) İŞLEMLERİ
      
      // Hissedar bakiyesinden düş
      if (shareDoc.exists()) {
         const share = shareDoc.data();
         let newPaidAmount = (share.paidAmount || 0) - amountToDeduct;
         if (newPaidAmount < 0) newPaidAmount = 0;
         
         const totalPrice = share.price || 0;
         let newStatus = 'partial';
         if (newPaidAmount === 0) newStatus = 'pending';
         if (totalPrice > 0 && newPaidAmount >= totalPrice) newStatus = 'paid';
         
         transaction.update(shareRef, {
           paidAmount: newPaidAmount,
           paymentStatus: newStatus
         });
      }

      // Silinenler arşivine ekle
      transaction.set(deletedPaymentRef, {
         ...paymentData,
         deletedAt: serverTimestamp(),
         deletedBy: deletedByEmail || 'Bilinmeyen Kullanıcı',
         originalShareId: shareId
      });
      
      // Orijinal dosyayı tamamen sil
      transaction.delete(paymentRef);
    });
  } catch (error) {
    console.error('Delete Payment Error:', error);
    throw new Error('Ödeme silinirken sistemsel bir hata oluştu.');
  }
};

/**
 * Tahsilat Geri Getirme (Silinenlerden çıkarıp asıl veritabanına ve bakiyeye geri yükler) - Sadece Super Admin
 */
export const restorePayment = async (paymentId) => {
  try {
    await runTransaction(db, async (transaction) => {
      // 1. DOKÜMAN REFERANSLARI
      const deletedPaymentRef = doc(db, 'deleted_payments', paymentId);
      const paymentRef = doc(db, 'payments', paymentId);

      // 2. OKUMA (GET) İŞLEMLERİ
      const deletedDoc = await transaction.get(deletedPaymentRef);
      
      if (!deletedDoc.exists()) {
         throw new Error("Çöp kutusunda bu makbuz bulunamadı. Tamamen silinmiş olabilir.");
      }
      
      const data = deletedDoc.data();
      const { deletedAt, deletedBy, originalShareId, ...originalPaymentData } = data;
      const shareId = originalShareId || originalPaymentData.shareId;
      const amountToAdd = originalPaymentData.amount || 0;

      const shareRef = doc(db, 'shares', shareId);
      const shareDoc = await transaction.get(shareRef);

      // 3. YAZMA (SET/UPDATE/DELETE) İŞLEMLERİ
      
      // Asıl tablolara geri yükle
      transaction.set(paymentRef, {
         ...originalPaymentData,
         restoredAt: serverTimestamp() // Geri yüklendiği tarihi sisteme işle
      });

      // Hissedar bakiyesine geri ekle
      if (shareDoc.exists()) {
         const share = shareDoc.data();
         const newPaidAmount = (share.paidAmount || 0) + amountToAdd;
         const totalPrice = share.price || 0;
         
         let newStatus = 'partial';
         if (newPaidAmount === 0) newStatus = 'pending';
         if (totalPrice > 0 && newPaidAmount >= totalPrice) newStatus = 'paid';

         transaction.update(shareRef, {
           paidAmount: newPaidAmount,
           paymentStatus: newStatus
         });
      }

      // Çöp kutusundan tamamen sil
      transaction.delete(deletedPaymentRef);
    });
  } catch(error) {
     console.error("Restore Payment Error: ", error);
     throw new Error("Makbuz geri getirilirken hata oluştu.");
  }
};

/**
 * Hissedar Silme (Süper Admin)
 * Hayvanın hissesini 1 eksiltir, ardından hissedarı siler.
 */
export const deleteShare = async (shareId, animalId) => {
  try {
    await runTransaction(db, async (transaction) => {
      const shareRef = doc(db, 'shares', shareId);
      const shareDoc = await transaction.get(shareRef);
      const shareWeight = shareDoc.exists() ? (Number(shareDoc.data().shareWeight) || 1) : 1;

      const animalRef = doc(db, 'animals', animalId);
      const animalDoc = await transaction.get(animalRef);
      if (animalDoc.exists()) {
         const animal = animalDoc.data();
         const newShareCount = Math.max(0, animal.shareCount - shareWeight);
         transaction.update(animalRef, { 
            shareCount: newShareCount,
            status: newShareCount < animal.totalShares ? 'active' : animal.status
         });
      }
      transaction.delete(shareRef);
    });
  } catch (error) {
    console.error('Delete Share Error:', error);
    throw new Error('Hissedar silinirken bir hata oluştu.');
  }
};

/**
 * Hissedar Transfer (Hayvan Değiştirme)
 */
export const transferShare = async (shareId, oldAnimalId, newAnimalId, newPrice) => {
  if (oldAnimalId === newAnimalId) {
    throw new Error("Aynı kurban seçildi.");
  }
  
  try {
    await runTransaction(db, async (transaction) => {
      // 1. DOKÜMAN REFERANSLARI
      const oldAnimalRef = doc(db, 'animals', oldAnimalId);
      const newAnimalRef = doc(db, 'animals', newAnimalId);
      const shareRef = doc(db, 'shares', shareId);

      // 2. TÜM VERİLERİ OKU (GETs - Firebase Kuralları Gereği Önce Yapılmalı)
      const oldAnimalDoc = await transaction.get(oldAnimalRef);
      const newAnimalDoc = await transaction.get(newAnimalRef);
      const shareDoc = await transaction.get(shareRef);

      // --- KONTROLLER ---
      if (!newAnimalDoc.exists()) throw new Error("Yeni kurban bulunamadı.");
      const newDocData = newAnimalDoc.data();
      
      if (!shareDoc.exists()) throw new Error("Hissedar bulunamadı.");
      const shareData = shareDoc.data();
      const shareWeight = Number(shareData.shareWeight) || 1;

      if (newDocData.shareCount + shareWeight > newDocData.totalShares) {
         throw new Error("Hedef kurbanda istenilen miktar kadar (" + shareWeight + ") boş hisse bulunmamaktadır!");
      }

      // 3. TÜM VERİLERİ YAZ (UPDATEs)
      
      // Eski Hayvanı Eksilt
      if (oldAnimalDoc.exists()) {
         const oldDocData = oldAnimalDoc.data();
         const newOldCount = Math.max(0, oldDocData.shareCount - shareWeight);
         transaction.update(oldAnimalRef, { 
             shareCount: newOldCount,
             status: newOldCount < oldDocData.totalShares ? 'active' : oldDocData.status 
         });
      }

      // Yeni Hayvanı Artır
      const newTargetCount = newDocData.shareCount + shareWeight;
      transaction.update(newAnimalRef, { 
          shareCount: newTargetCount,
          status: newTargetCount >= newDocData.totalShares ? 'completed' : 'active'
      });

      // Hissedarı Güncelle
      const currentPaid = shareData.paidAmount || 0;
      let newStatus = 'partial';
      if (newPrice > 0 && currentPaid >= newPrice) newStatus = 'paid';
      if (currentPaid === 0) newStatus = 'pending';

      transaction.update(shareRef, {
         animalId: newAnimalId,
         price: Number(newPrice),
         paymentStatus: newStatus
      });
    });
  } catch (error) {
    console.error("Transfer Error:", error);
    throw error;
  }
};

/**
 * Kurbanı Tamamen Silme (Sadece Super Admin)
 * Kurbana bağlı tüm hissedarlar (shares), satış iptal edilmeden "unassigned" olarak boşa çıkartılır.
 * Böylece ödemeler ve cüzdan bakiyeleri sapasağlam kalır.
 */
export const deleteAnimal = async (animalId) => {
  try {
    const q = query(collection(db, 'shares'), where('animalId', '==', animalId));
    const sharesSnap = await getDocs(q);

    const batch = writeBatch(db);

    // 1. Hayvanı Sil
    const animalRef = doc(db, 'animals', animalId);
    batch.delete(animalRef);

    // 2. Ait olan hissedarların animalId'lerini unassigned yap (boşa düşür)
    sharesSnap.forEach((docSnap) => {
      batch.update(docSnap.ref, { 
         animalId: 'unassigned',
         updatedAt: serverTimestamp() 
      });
    });

    await batch.commit();
  } catch (error) {
    console.error('Delete Animal Error:', error);
    throw new Error('Kurban silinirken sistemsel bir hata oluştu.');
  }
};
